"use client";

import { useEffect, useState } from "react";

import Link from "next/link";

import { simpleCategories } from "@/config/components";

export const categories = {
	All: [],
	New: [],
	Cards: ["card", "deck", "stack", "flip", "tile"],
	Buttons: ["button", "btn", "click", "press"],
	Text: ["text", "label", "type", "word", "letter", "font"],
	Backgrounds: [
		"background",
		"bg",
		"back",
		"vignette",
		"veil",
		"gradient",
		"blur",
		"star",
		"matrix",
	],
	Carousels: ["carousel", "slider", "gallery", "reel", "marquee"],
	Navigation: [
		"nav",
		"navbar",
		"menu",
		"sidebar",
		"tabs",
		"cta",
		"scrolllink",
	],
	Loaders: ["loader", "loading", "spinner", "preloader"],
	Accordions: ["accordion", "faq", "expand", "collapse"],
	Images: [
		"image",
		"zoom",
		"viewer",
		"ripple",
		"lens",
		"macbook",
		"media",
		"model",
		"canvas",
	],
	"Scroll Effects": [
		"scroll",
		"parallax",
		"sticky",
		"reveal",
		"float",
		"island",
	],
	Cursors: ["cursor", "pointer", "mouse", "trail", "target", "eyes"],
	"Grids & Layouts": ["grid", "layout", "bento", "masonry", "columns", "rows"],
	"Forms & Inputs": [
		"form",
		"input",
		"search",
		"checkbox",
		"modal",
		"select",
		"field",
		"editor",
	],
	Testimonials: ["testimonial", "quote", "review"],
	"Animations & FX": [
		"animated",
		"effect",
		"motion",
		"hover",
		"focus",
		"swap",
		"dynamic",
		"spring",
		"trail",
		"morph",
		"transition",
		"sparkle",
		"glow",
		"shine",
		"fuzzy",
		"flip",
		"highlight",
		"zoom",
	],
	"3D & Canvas": ["3d", "globe", "canvas", "model", "viewer", "lens"],
	"Data & Tables": ["table", "chart", "list", "view", "ticker"],
	Utilities: ["provider", "context", "helper", "tool", "sandbox"],
	Videos: ["video", "movie", "player"],
};

const credits = [
	"https://www.fancycomponents.dev",
	"https://www.ui-layouts.com",
	"https://github.com/pqoqubbw/icons",
	"https://www.joshuawootonn.com/",
	"https://skiper-ui.com/",
	"https://www.hover.dev",
	"https://kokonutui.com/",
	"https://zenui.net/",
	"https://ui.noxhd.com",
	"https://www.reactbits.dev/",
	"https://ui.noxhd.com/",
	"https://www.serenity-ui.com/",
	"https://cuicui.day/",
	"https://starui.link/",
	"https://berlix.vercel.app/",
	"https://ui.aceternity.com/",
	"https://www.cult-ui.com/",
	"https://auraui.vercel.app/",
	"https://www.sparkui.me/",
	"https://www.kibo-ui.com/",
	"https://www.smoothui.dev/",
	"https://geist.vercel.app/",
	"https://eclairui.gopx.dev/",
	"https://namer-ui.netlify.app/",
	"https://animata.design/",
	"https://ground.bossadizenith.me",
	"https://www.hover.dev/",
	"https://www.edilozi.pro/",
	"https://www.pldkhoa.dev",
	"https://karrix.dev/",
	"https://github.com/PhanDangKhoa96",
	"https://www.diceui.com",
	"https://motion-primitives.com/",
	"https://codepen.io/jh3y/pen/gOEgxbd",
	"https://pro.lightswind.com/",
	"https://aetherui.in/",
	"https://21st.dev/",
	"https://www.vyomaui.design",
];

export default function HomePage({
	files,
	newFiles,
}: {
	files: { name: string; isNew: Date }[];
	newFiles: { name: string }[];
}) {
	const [hovered, setHovered] = useState<string | null>(null);

	const [items, setItems] = useState([""]);
	const [subcategories, setSubcategories] = useState([""]);
	const [newItems, setNewItems] = useState<any[]>([]);

	return (
		<main className="min-h-screen bg-zinc-950 text-white p-8">
			<section>
				<h2>Heavy animations</h2>
				<div className="grid grid-cols-7 h-full min-h-screen">
					<div className="grid grid-cols-2 col-span-7 md:col-span-3 sm:grid-cols-3 md:grid-cols-4 gap-4 max-w-4xl mx-auto h-fit">
						{Object.entries(categories).map(
							([category, subcategories], i) => {
								// Build query string from subcategories
								const queryString =
									category === "All"
										? ""
										: "?" +
											subcategories
												.map(
													(sub) =>
														`subcategory=${encodeURIComponent(sub)}`
												)
												.join("&");

								// Build href
								const href =
									category === "All"
										? "/all"
										: `/type/${encodeURIComponent(category.toLowerCase().replace(/\s&\s|\s/g, "-"))}${queryString}`;

								const categoryTotal = files.filter(
									({ name }) =>
										name.includes(category) ||
										!!subcategories
											.map((filter) => name.includes(filter))
											.filter((item) => !!item).length
								);

								const newInsideContainer = categoryTotal.filter(
									(item) =>
										!!newFiles.map(
											(newfile) => newfile.name === item.name
										).length
								);

								if (category === "New") {
									return (
										<Link
											onMouseEnter={() => {
												setItems(newFiles.map((item) => item.name));
											}}
											onMouseLeave={() => {
												setHovered(null);
												setItems([]);
												setSubcategories([]);
												setNewItems([]);
											}}
											key={"all-new"}
											href={"new"}
											className={`rounded-2xl h-fit relative px-6 py-4 bg-zinc-800 hover:bg-zinc-700 transition-colors text-center font-medium shadow-md ${"text-white"}`}
										>
											NEW
											<br />
											{newFiles.length}
										</Link>
									);
								}

								return (
									<Link
										key={category}
										onMouseEnter={() => {
											setHovered(category);
											setItems(
												categoryTotal.map((item) => item.name)
											);
											setSubcategories(subcategories);
											!!newInsideContainer.length &&
												newInsideContainer.forEach((item) =>
													setNewItems((prev) => [...prev, item])
												);
										}}
										onMouseLeave={() => {
											setHovered(null);
											setItems([]);
											setSubcategories([]);
											setNewItems([]);
										}}
										href={href}
										className={`rounded-2xl h-fit relative px-6 py-4 bg-zinc-800 hover:bg-zinc-700 transition-colors text-center font-medium shadow-md ${
											category === "All"
												? "text-red-400"
												: "text-white"
										}`}
									>
										{category === "All" && hovered === "All"
											? "will cause lag"
											: category}
										<br />
										{category === "All"
											? files.length
											: categoryTotal.length}
										<br />
										{!!newInsideContainer.length && (
											<span className="absolute -top-0 -right-0 rotate-45">
												⭐
											</span>
										)}
									</Link>
								);
							}
						)}
						<span
							className={`rounded-2xl h-fit relative px-6 py-4 bg-zinc-800 transition-colors text-center font-medium shadow-md ${"text-white"}`}
						>
							⭐: Component added within a week
							<br />
							All: May cause lag
						</span>
					</div>
					<div className="hidden md:block col-span-2">
						<span className="text-lg border-b-2 border-white">
							Matching Items:
						</span>
						<ul className="flex flex-col flex-wrap h-full max-h-[50vh] overflow-hidden">
							{items.map((item) => (
								<li>{item.replace(".json", "")}</li>
							))}
						</ul>
					</div>
					<div className="hidden md:block col-span-1">
						<span className="text-lg border-b-2 border-white">
							Filter Match:
						</span>
						<ul className="flex flex-col flex-wrap h-full overflow-hidden">
							{subcategories.map((item) => (
								<li>{item.replace(".json", "")}</li>
							))}
						</ul>
					</div>
					<div className="hidden md:block col-span-1">
						<div className="hidden md:block ">
							<span className="text-lg border-b-2 border-white">
								New Items:
							</span>
							{!!newItems.length && (
								<ul className="flex flex-col flex-wrap h-full overflow-hidden">
									{newItems.map((item) => (
										<li>{item.name.replace(".json", "")}</li>
									))}
								</ul>
							)}
						</div>
					</div>
				</div>
			</section>
			<section className="h-[50vh]">
				<h2>Credits ({credits.length}):</h2>
				<div className="flex flex-wrap gap-2">
					{credits.sort().map((item, i) => (
						<a
							href={item}
							target="_blank"
							rel="noopener noreferrer"
							className={`rounded-2xl h-fit relative px-6 py-4 bg-zinc-800 hover:bg-zinc-700 transition-colors text-center font-medium shadow-md ${"text-white"}`}
						>
							{item
								.replace("https://", "")
								.replace("www.", "")
								.replace(".dev", "")
								.replace(".com", "")
								.replace(".net", "")
								.replace(".me", "")
								.replace(".io", "")
								.replace(".app", "")
								.replace("/", "")
								.replace(".design", "")}
						</a>
					))}
				</div>
			</section>
			<section>
				<h3>Basic Animations</h3>
				<div className="grid grid-cols-2 col-span-7 md:col-span-3 sm:grid-cols-3 md:grid-cols-4 gap-4 max-w-4xl mx-auto h-fit">
					{simpleCategories
						.sort((a, b) => {
							if (a.isNew && !b.isNew) return -1;
							if (!a.isNew && b.isNew) return 1;
							return 0;
						})
						.map(({ slug, isNew, name }) => (
							<Link
								href={"/" + slug}
								className="rounded-2xl h-fit relative px-6 py-4 bg-zinc-800 hover:bg-zinc-700 transition-colors text-center font-medium shadow-md text-white"
								tabIndex={-1}
							>
								{isNew && (
									<svg
										xmlns="http://www.w3.org/2000/svg"
										className="absolute top-5 left-5 z-10 text-zinc-950 dark:text-zinc-200"
										width="29"
										height="13"
										fill="currentColor"
									>
										<title>New</title>
										<path d="M18.805 12.406c-.326 0-.597-.145-.811-.434-.206-.299-.308-.826-.308-1.582 0-.793.014-1.563.041-2.31.029-.756.08-1.433.154-2.03.084-.597.197-1.073.337-1.428s.322-.532.546-.532c.158 0 .298.089.42.266.12.168.181.392.181.672 0 .29-.027.677-.083 1.162a46.698 46.698 0 0 1-.21 1.568c-.075.55-.14 1.087-.197 1.61-.055.513-.084.947-.084 1.302 0 .15.005.27.014.364.02.084.047.126.085.126.14 0 .289-.121.447-.364a7.49 7.49 0 0 0 .532-.98c.187-.41.383-.859.588-1.344.206-.495.416-.985.63-1.47.215-.485.434-.929.659-1.33.224-.41.448-.737.672-.98.224-.252.443-.378.658-.378.252 0 .485.121.7.364.224.233.335.57.335 1.008 0 .13-.018.36-.056.686a60.22 60.22 0 0 1-.14 1.092l-.168 1.232c-.055.401-.102.76-.14 1.078a6.3 6.3 0 0 0-.055.658c0 .485.046.728.14.728.26 0 .536-.164.825-.49a7.67 7.67 0 0 0 .896-1.288c.308-.532.612-1.106.91-1.722l.883-1.792c.13-.299.238-.5.322-.602.093-.112.186-.168.28-.168.13 0 .252.07.364.21.12.14.181.331.181.574 0 .084-.004.168-.014.252-.009.075-.032.159-.07.252-.317.84-.657 1.62-1.021 2.338-.364.71-.738 1.33-1.12 1.862-.374.522-.742.933-1.107 1.232-.364.29-.7.434-1.008.434-.532 0-.947-.15-1.246-.448-.289-.308-.433-.719-.433-1.232 0-.215.014-.495.041-.84.038-.345.08-.719.127-1.12.056-.401.111-.789.168-1.162.055-.373.102-.7.14-.98.046-.28.074-.471.084-.574.009-.13-.005-.196-.042-.196-.038 0-.113.121-.224.364-.103.233-.23.532-.378.896-.14.355-.29.723-.448 1.106-.178.42-.383.882-.616 1.386-.224.495-.467.966-.729 1.414a5.366 5.366 0 0 1-.797 1.106c-.28.29-.565.434-.855.434Z" />
										<path d="M16.554 5.966c0 .299-.094.593-.28.882-.187.28-.434.55-.742.812-.308.252-.649.49-1.022.714-.374.224-.747.42-1.12.588-.374.168-.714.303-1.022.406a4.88 4.88 0 0 0-.112.966c0 .177.042.303.126.378.084.075.214.112.392.112.392 0 .807-.07 1.246-.21.438-.14.886-.322 1.344-.546.457-.224.896-.462 1.316-.714.42-.252.802-.49 1.148-.714.354-.224.648-.406.882-.546.233-.14.387-.21.462-.21.112 0 .196.037.252.112a.324.324 0 0 1 .098.238c0 .075-.117.22-.35.434-.224.215-.528.467-.91.756-.383.28-.812.57-1.288.868-.476.299-.97.579-1.484.84-.504.261-.994.471-1.47.63-.467.159-.887.238-1.26.238-.486 0-.864-.182-1.134-.546-.27-.364-.406-.929-.406-1.694 0-.513.065-1.031.196-1.554.14-.523.326-1.022.56-1.498a7.77 7.77 0 0 1 .798-1.288c.298-.383.611-.681.938-.896.326-.224.644-.336.952-.336.588 0 1.05.163 1.386.49.336.327.504.756.504 1.288ZM12.662 8.29c.29-.177.588-.364.896-.56.308-.196.592-.397.854-.602.261-.205.471-.42.63-.644a1.21 1.21 0 0 0 .252-.714c0-.345-.187-.518-.56-.518-.234 0-.48.15-.742.448-.252.29-.495.667-.728 1.134-.234.467-.434.952-.602 1.456ZM7.326 12.84a.664.664 0 0 1-.518-.238c-.13-.159-.196-.42-.196-.784 0-.121.014-.252.042-.392.028-.15.08-.299.154-.448a35.616 35.616 0 0 1-1.176-3.374 36.19 36.19 0 0 1-.812-3.43 32.977 32.977 0 0 0-.854 1.918A88.035 88.035 0 0 0 2.594 9.76c-.187.541-.35 1.008-.49 1.4-.15.467-.299.78-.448.938-.15.168-.304.252-.462.252-.215 0-.378-.051-.49-.154C.6 12.093.55 11.911.55 11.65c0-.196.051-.5.154-.91.102-.41.242-.887.42-1.428.186-.541.396-1.11.63-1.708.233-.597.48-1.18.742-1.75.27-.57.541-1.083.812-1.54.27-.467.527-.835.77-1.106.252-.28.48-.42.686-.42.214 0 .396.042.546.126.158.084.331.303.518.658.121.243.261.597.42 1.064.158.457.317.975.476 1.554.158.57.312 1.157.462 1.764.15.597.28 1.153.392 1.666l.98-4.536c.28-1.307.494-2.329.644-3.066.158-.737.242-1.125.252-1.162.056-.243.116-.392.182-.448a.418.418 0 0 1 .28-.084c.168 0 .331.07.49.21.168.13.252.313.252.546 0 .112-.028.369-.084.77-.047.401-.121.905-.224 1.512a45.805 45.805 0 0 1-.364 2.002 58.78 58.78 0 0 1-.518 2.282 41.448 41.448 0 0 1-.658 2.338c-.234.765-.49 1.489-.77 2.17-.103.261-.224.439-.364.532-.13.103-.248.154-.35.154Z" />
									</svg>
								)}
								{name}
							</Link>
						))}
				</div>
			</section>
		</main>
	);
}
